// #667 Phase 3 of #664: backend `/api/v1/health/full` を polling し、Stripe degraded 時に
// 全 route を MaintenancePage で覆う Guard component。App.tsx の root で <Routes> を wrap。
//
// 設計:
// - 30s 間隔 polling (= setInterval、SWR 未導入のため自前)
// - HTTP error / network error も degraded とみなす (= API 自体落ちてる時に通常画面を
//   見せ続けるのは UX 悪い)。ただし**理由ごとに文言を分ける** (MaintenancePage 参照)。
//   以前は通信断でも「決済システムに障害」と出ており、客の電波の問題を
//   こちらの決済障害だと誤認させていた
// - 初回 fetch 完了までは healthy 仮定 (= 起動直後に flicker しないように)
// - 端末がオンラインに戻ったら次の polling を待たずに再確認する
//
// 期待 response:
//   { healthy: bool, reason: string | null, last_check_at: number | null,
//     recent_failures: number }
import { useCallback, useEffect, useRef, useState } from 'react'
import MaintenancePage from './MaintenancePage'
import { fetchMaintenanceNotice, type MaintenanceNotice } from '../lib/maintenanceNotice'

interface HealthFull {
  healthy: boolean
  reason: string | null
  last_check_at: number | null
  recent_failures: number
}

const POLL_INTERVAL_MS = 30_000

export function MaintenanceGuard({ children }: { children: React.ReactNode }) {
  const [degraded, setDegraded] = useState(false)
  const [reason, setReason] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  // #2692 意図して止めたときの文言。API が届かないとき (403・通信断) に Firestore から読む。
  // API は止まっていても Firestore は別の口なので読める。無ければ null = 今までの文言
  const [notice, setNotice] = useState<MaintenanceNotice | null>(null)
  const cancelledRef = useRef(false)

  const loadNotice = useCallback(() => {
    void fetchMaintenanceNotice().then((n) => {
      if (!cancelledRef.current) setNotice(n)
    })
  }, [])

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/health/full', { cache: 'no-store' })
      if (!res.ok) {
        // API 自体落ちている時 (5xx / 4xx) は degraded 扱い
        if (!cancelledRef.current) {
          setDegraded(true)
          setReason(`http_${res.status}`)
          loadNotice()
        }
        return
      }
      const data = (await res.json()) as HealthFull
      if (!cancelledRef.current) {
        setDegraded(!data.healthy)
        setReason(data.reason)
        // API に届いた = 止めているのではない。healthy でなくても (決済の監視が degraded)、
        // 文言は障害の方 (「お支払いを停止しています」と ref) に戻す
        setNotice(null)
      }
    } catch {
      // network error も degraded (= 端末がオフライン or サーバに到達できない)
      if (!cancelledRef.current) {
        setDegraded(true)
        setReason('network_error')
        loadNotice()
      }
    }
  }, [loadNotice])

  useEffect(() => {
    cancelledRef.current = false
    fetchHealth()
    const id = setInterval(fetchHealth, POLL_INTERVAL_MS)
    window.addEventListener('online', fetchHealth)
    return () => {
      cancelledRef.current = true
      clearInterval(id)
      window.removeEventListener('online', fetchHealth)
    }
  }, [fetchHealth])

  // 再読み込み (location.reload) にしないのは、オフライン中に押すとブラウザの
  // 「接続されていません」画面に飛んでアプリに戻れなくなるため。
  // health を取り直して healthy なら覆いが外れる。
  const retry = useCallback(async () => {
    setChecking(true)
    try {
      await fetchHealth()
    } finally {
      if (!cancelledRef.current) setChecking(false)
    }
  }, [fetchHealth])

  // **children は外さずに上から覆う。** 以前は children を MaintenancePage に差し替えて
  // いたため、決済の途中 (カード確定 → 記録 API の応答待ち) に polling が 1 回落ちる
  // だけで QRScan / Stripe のシートが unmount され、復帰後は何事も無かったような
  // 決済画面に戻っていた (= 「完了を確認できませんでした」も消え、もう一度払わせる)。
  // 覆うだけなら復帰したときに画面の状態がそのまま残る。
  return (
    <>
      {children}
      {degraded && (
        // 各画面のモーダル (認証系 z-[200]、地図 z-[1100]、Leaflet ~1000) より上
        <div className="fixed inset-0 z-[2000] overflow-y-auto" role="alertdialog" aria-modal="true">
          <MaintenancePage reason={reason} notice={notice} onRetry={retry} checking={checking} />
        </div>
      )}
    </>
  )
}
